function loadImageElement(source) {
    return new Promise((resolve, reject) => {
        const img = new Image()
        let objectUrl = null

        if (source instanceof Blob) {
            objectUrl = URL.createObjectURL(source)
            img.src = objectUrl
        } else if (
            typeof source === 'string' &&
            source.startsWith('blob:')
        ) {
            // Do not set crossOrigin on blob URLs — it breaks loading in most browsers.
            img.src = source
        } else if (typeof source === 'string') {
            img.crossOrigin = 'anonymous'
            img.src = source
        } else {
            reject(new Error('Unsupported image source'))
            return
        }

        img.onload = () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }
            resolve(img)
        }
        img.onerror = () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl)
            }
            reject(new Error('Failed to load image for rotation'))
        }
    })
}

const mediaLocalFileCache = new Map()

export function cacheMediaLocalFile(key, file) {
    if (key != null && file instanceof File) {
        mediaLocalFileCache.set(String(key), file)
    }
}

export function getCachedMediaLocalFile(item = {}) {
    const keys = [
        item?.MediaId,
        item?.Unique_Id,
        item?.id,
        item?.FileKey,
        item?.url,
    ].filter(k => k != null && String(k) !== '')

    for (let i = 0; i < keys.length; i += 1) {
        const cached = mediaLocalFileCache.get(String(keys[i]))
        if (cached instanceof File) {
            return cached
        }
    }
    return null
}

export function updateCachedMediaLocalFile(item, file) {
    if (!(file instanceof File)) {
        return
    }
    const keys = [
        item?.MediaId,
        item?.Unique_Id,
        item?.id,
        item?.FileKey,
        item?.url,
    ].filter(k => k != null && String(k) !== '')

    keys.forEach(key => {
        mediaLocalFileCache.set(String(key), file)
    })
}

export function cacheMediaUploadFiles(files = []) {
    files.forEach(ele => {
        const rawFile = getRawFileFromMediaItem(ele)
        if (!rawFile) {
            return
        }
        cacheMediaLocalFile(ele?.id, rawFile)
        cacheMediaLocalFile(ele?.MediaId, rawFile)
        cacheMediaLocalFile(ele?.FileKey, rawFile)
    })
}

export function linkUploadedMediaCache(sources = [], uploaded = []) {
    sources.forEach((source, index) => {
        const rawFile = getRawFileFromMediaItem(source)
        if (!rawFile) {
            return
        }
        const uploadedItem = uploaded[index]
        if (uploadedItem) {
            updateCachedMediaLocalFile(uploadedItem, rawFile)
        }
        updateCachedMediaLocalFile(source, rawFile)
    })
}

export function getRawFileFromMediaItem(item) {
    if (item?.file?.file instanceof File) {
        return item.file.file
    }
    if (item?.file instanceof File) {
        return item.file
    }
    if (item?.file instanceof Blob) {
        return item.file
    }
    return null
}

/**
 * Rotate an image File/Blob/URL by the given degrees (default 90° clockwise).
 * Returns a new File and blob preview URL.
 */
export async function rotateImageFile(
    source,
    degrees = 90,
    fileName = 'rotated-image.jpg',
) {
    const image = await loadImageElement(source)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const isQuarterTurn = Math.abs(degrees) % 180 !== 0

    canvas.width = isQuarterTurn ? image.height : image.width
    canvas.height = isQuarterTurn ? image.width : image.height

    ctx.translate(canvas.width / 2, canvas.height / 2)
    ctx.rotate((degrees * Math.PI) / 180)
    ctx.drawImage(image, -image.width / 2, -image.height / 2)

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            result => {
                if (result) {
                    resolve(result)
                } else {
                    reject(new Error('Failed to rotate image'))
                }
            },
            'image/jpeg',
            0.92,
        )
    })

    const name =
        source instanceof File
            ? source.name
            : fileName
    const file = new File([blob], name, { type: 'image/jpeg' })
    const url = URL.createObjectURL(blob)
    return { file, url }
}

/** Rotate using local File only — no remote URL fetch. */
export async function rotateImageFileFromMediaItem(item, degrees = 90) {
    const rawFile =
        getRawFileFromMediaItem(item) || getCachedMediaLocalFile(item)
    const fileName =
        rawFile?.name ||
        item?.file?.name ||
        item?.FileKey ||
        'rotated-image.jpg'

    if (rawFile) {
        return rotateImageFile(rawFile, degrees, fileName)
    }

    const blobUrl = item?.url
    if (typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
        const response = await fetch(blobUrl)
        const blob = await response.blob()
        return rotateImageFile(blob, degrees, fileName)
    }

    throw new Error('Local image file not available for rotation')
}

export function base64ToBlobUrl(base64Data, contentType = '') {
    const byteCharacters = atob(base64Data.split(',')[1]) // Decode Base64 data
    const byteArrays = []
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512)
        const byteNumbers = new Array(slice.length)
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
    }
    const blob = new Blob(byteArrays, {
        type: contentType || base64Data.split(';')[0].split(':')[1],
    })
    return URL.createObjectURL(blob) // Generate Blob URL
}
